/**
 * Phase 14.5 Step 2 — Target Pk section (Phase 12).
 *
 * Bayesian-optimised search for a target Pk threshold. Reads the
 * pk-campaigns registry for the variable budget multi-select. Total-
 * runs readout enforces the 5000-run cap (max_iterations × samples_per_iteration).
 */
import { useMemo } from "react";
import { Target } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TOTAL_RUNS_CAP = 5000;
const DEFAULTS = {
    target_pk: 0.6,
    max_iterations: 30,
    samples_per_iteration: 20,
    lambda_penalty: 0.1,
};

function _allVarIds(registry) {
    if (!registry) return [];
    const arr = registry.variables || registry.entries || registry.catalog || [];
    return Array.isArray(arr) ? arr.map((v) => v?.id).filter(Boolean) : [];
}

export default function TargetPkSection({
    enabled = false,
    onToggleEnabled,
    value,
    onChange,
    registry,
}) {
    const allIds = useMemo(() => _allVarIds(registry), [registry]);
    const target_pk = Number(value?.target_pk ?? DEFAULTS.target_pk);
    const max_iterations = Number(value?.max_iterations ?? DEFAULTS.max_iterations);
    const samples_per_iteration = Number(value?.samples_per_iteration ?? DEFAULTS.samples_per_iteration);
    const lambda_penalty = Number(value?.lambda_penalty ?? DEFAULTS.lambda_penalty);
    const variable_budget = Array.isArray(value?.variable_budget)
        ? value.variable_budget
        : [];

    const totalRuns = Math.max(0, max_iterations * samples_per_iteration);
    const overCap = totalRuns > TOTAL_RUNS_CAP;
    const setField = (k, v) => onChange?.({ ...(value || {}), [k]: v });
    const toggleBudget = (id) => {
        const next = variable_budget.includes(id)
            ? variable_budget.filter((x) => x !== id)
            : [...variable_budget, id];
        setField("variable_budget", next);
    };

    return (
        <section
            className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-4"
            data-testid="section-target-pk"
            data-enabled={enabled ? "1" : "0"}
        >
            <div className="flex items-start gap-3">
                <Switch
                    checked={enabled}
                    onCheckedChange={onToggleEnabled}
                    data-testid="toggle-target-pk"
                    aria-label="Enable target Pk"
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <Target className="size-4 text-ux-text-3" />
                        <span className="text-sm font-medium text-ux-text-1">Target Pk</span>
                    </div>
                    <p className="text-xs text-ux-text-3">
                        Bayesian-optimised search for a target Pk under a min-distance objective
                        with λ penalty for normalised distance.
                    </p>
                </div>
            </div>

            {enabled && (
                <div className="mt-4 space-y-4" data-testid="target-pk-details">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                            <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                                Target Pk
                            </Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                max="0.99"
                                value={target_pk}
                                onChange={(e) => setField("target_pk", parseFloat(e.target.value || "0"))}
                                data-testid="target-pk-target"
                            />
                        </div>
                        <div>
                            <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                                λ penalty
                            </Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="10"
                                value={lambda_penalty}
                                onChange={(e) => setField("lambda_penalty", parseFloat(e.target.value || "0"))}
                                data-testid="target-pk-lambda"
                            />
                        </div>
                        <div>
                            <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                                Max iterations
                            </Label>
                            <Input
                                type="number"
                                min="1"
                                max="100"
                                value={max_iterations}
                                onChange={(e) => setField("max_iterations", parseInt(e.target.value || "0", 10) || 0)}
                                data-testid="target-pk-max-iterations"
                            />
                        </div>
                        <div>
                            <Label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                                Samples per iteration
                            </Label>
                            <Input
                                type="number"
                                min="1"
                                max="200"
                                value={samples_per_iteration}
                                onChange={(e) => setField("samples_per_iteration", parseInt(e.target.value || "0", 10) || 0)}
                                data-testid="target-pk-samples-per-iteration"
                            />
                        </div>
                    </div>
                    <div
                        className={[
                            "rounded-md border px-3 py-2 text-xs",
                            overCap
                                ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                                : "border-ux-border-1 bg-ux-surface-2/40 text-ux-text-3",
                        ].join(" ")}
                        data-testid="target-pk-total-runs"
                        data-over-cap={overCap ? "1" : "0"}
                    >
                        Total runs: <strong className="text-ux-text-2">{totalRuns.toLocaleString()}</strong>{" "}
                        ({max_iterations} × {samples_per_iteration}) · cap {TOTAL_RUNS_CAP.toLocaleString()}
                        {overCap && (
                            <span className="ml-2">— exceeds 5000-run cap; reduce iterations or samples.</span>
                        )}
                    </div>
                    {allIds.length > 0 && (
                        <details className="text-xs">
                            <summary className="cursor-pointer text-ux-text-3" data-testid="target-pk-budget-toggle">
                                Customise variable budget ({variable_budget.length || "default top-10"} selected)
                            </summary>
                            <div
                                className="mt-2 grid grid-cols-2 gap-1 sm:grid-cols-3"
                                data-testid="target-pk-budget-grid"
                            >
                                {allIds.map((id) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => toggleBudget(id)}
                                        data-testid={`target-pk-budget-chip-${id}`}
                                        className={[
                                            "truncate rounded border px-2 py-1 text-left",
                                            variable_budget.includes(id)
                                                ? "border-ux-text-1 bg-ux-surface-2 text-ux-text-1"
                                                : "border-ux-border-1 bg-ux-surface-1 text-ux-text-3 hover:bg-ux-surface-2/60",
                                        ].join(" ")}
                                    >
                                        {id}
                                    </button>
                                ))}
                            </div>
                        </details>
                    )}
                </div>
            )}
        </section>
    );
}
